import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, ArrowRight, CheckCircle, Clock, AlertTriangle,
  Bell, Shield, Play, Home, Pill, Phone,
  TrendingUp, Activity, Heart, Users, Zap,
  Smile, Meh, AlertOctagon, Check, AlertCircle, ArrowDown,
  MousePointer, Brain, User
} from 'lucide-react';
import '../styles/design-system.css';

// ── Data ─────────────────────────────────────────────────────────────────────

const PATIENT_STEPS = [
  {
    icon: Smile,
    whoColor: '#97f2f3', whoText: '#007072',
    label: 'Open the app',
    description: 'The first thing Alice sees — above everything else — is a mood check-in. Three buttons. No forms.',
    visual: <MoodCheckInMock />,
  },
  {
    icon: AlertTriangle,
    whoColor: '#97f2f3', whoText: '#007072',
    label: 'Report an issue',
    description: 'She taps "I don\'t feel well." One more tap to say it\'s "Needs attention." Optionally, she adds a sentence in plain words.',
    visual: <QuickCaptureMock />,
  },
  {
    icon: CheckCircle,
    whoColor: '#97f2f3', whoText: '#007072',
    label: 'Gets reassured immediately',
    description: 'The success screen tells Alice exactly what happens next. She\'s not left wondering. She doesn\'t need to call anyone.',
    visual: <SuccessMock />,
  },
];

const DOCTOR_STEPS = [
  {
    icon: Bell,
    whoColor: '#ffdea8', whoText: '#5f4300',
    label: 'Opens the priority queue',
    description: 'Dr. Chen doesn\'t see a list of alerts. She sees action cards — one per patient, ranked by urgency, with a one-sentence reason and an AI-generated clinical summary.',
    visual: <ActionCardMock />,
  },
  {
    icon: Brain,
    whoColor: '#ffdea8', whoText: '#5f4300',
    label: 'Reads the AI context',
    description: 'The card shows condition, days since discharge, what was reported, and a Groq-powered 2-sentence clinical interpretation. A 14-point risk sparkline shows the trend at a glance.',
    visual: <ContextMock />,
  },
  {
    icon: Zap,
    whoColor: '#ffdea8', whoText: '#5f4300',
    label: 'Executes in one click',
    description: 'She clicks Execute. The system logs the action. The patient is notified in real time. If a follow-up is scheduled, the patient gets an email confirmation and the caregiver portal updates.',
    visual: <ExecuteMock />,
  },
];

// ── Mock UI components ────────────────────────────────────────────────────────

const MOODS = [
  { Icon: Smile,        label: 'Good',      bg: '#f0fdf4', c: '#166534', border: '#dcfce7' },
  { Icon: Meh,          label: 'Not Great', bg: '#fefce8', c: '#854d0e', border: '#fef9c3' },
  { Icon: AlertOctagon, label: 'Need Help', bg: '#fef2f2', c: '#991b1b', border: '#fee2e2' },
];

function MoodCheckInMock() {
  const [selected, setSelected] = useState(null);
  const feedback = {
    Good:       { Icon: Check,        color: '#00696b', text: 'Noted. Keep it up!' },
    'Not Great':{ Icon: ArrowDown,    color: '#00696b', text: 'Tell us more below' },
    'Need Help':{ Icon: AlertCircle,  color: '#dc2626', text: 'Alerting your care team now...' },
  };
  return (
    <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <p className="text-xs font-bold mb-1" style={{ color: '#727783' }}>Good morning, Alice</p>
      <p className="font-headline font-bold text-base mb-4" style={{ color: '#191c1e' }}>How are you feeling right now?</p>
      <div className="grid grid-cols-3 gap-2">
        {MOODS.map(({ Icon, label, bg, c, border }) => (
          <button
            key={label}
            onClick={() => setSelected(label)}
            className="flex flex-col items-center py-4 rounded-xl border-2 transition-all active:scale-95"
            style={{
              background: selected === label ? bg : '#f9f9fd',
              borderColor: selected === label ? border : 'transparent',
            }}
          >
            <Icon className="w-6 h-6 mb-1.5" style={{ color: selected === label ? c : '#9ca3af' }} />
            <span className="text-xs font-bold" style={{ color: selected === label ? c : '#727783' }}>{label}</span>
          </button>
        ))}
      </div>
      {selected && (() => {
        const { Icon: FbIcon, color, text } = feedback[selected];
        return (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            <FbIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
            <p className="text-xs font-semibold" style={{ color }}>{text}</p>
          </div>
        );
      })()}
    </div>
  );
}

function QuickCaptureMock() {
  const [severity, setSeverity] = useState('high');
  return (
    <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div className="flex items-center gap-2.5 p-2.5 rounded-xl border-2 mb-3" style={{ background: '#fee2e2', borderColor: '#fca5a5' }}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: '#dc2626' }} />
        <span className="text-xs font-bold" style={{ color: '#991b1b' }}>I don't feel well</span>
        <button className="ml-auto text-xs font-bold" style={{ color: '#dc2626' }}>Change</button>
      </div>
      <p className="text-xs font-bold mb-2" style={{ color: '#424752' }}>How urgent is this?</p>
      <div className="grid grid-cols-2 gap-1.5 mb-3">
        {[['low','Not urgent'],['medium','Concerning'],['high','Needs attention'],['urgent','Urgent']].map(([v,l]) => (
          <button key={v} onClick={() => setSeverity(v)} className="p-2 rounded-lg border text-xs font-semibold transition-all" style={{
            background: severity === v ? (v === 'urgent' ? '#fee2e2' : v === 'high' ? '#fff7ed' : v === 'medium' ? '#fefce8' : '#f0fdf4') : '#f9f9fd',
            borderColor: severity === v ? (v === 'urgent' ? '#fca5a5' : v === 'high' ? '#fed7aa' : v === 'medium' ? '#fef08a' : '#86efac') : '#e7e8eb',
            color: severity === v ? (v === 'urgent' ? '#dc2626' : v === 'high' ? '#c2410c' : v === 'medium' ? '#854d0e' : '#166534') : '#727783',
          }}>{l}</button>
        ))}
      </div>
      <textarea readOnly value="my ankles are swollen again and i cant catch my breath walking to the bathroom" className="w-full p-2.5 rounded-xl text-xs resize-none" rows={2} style={{ background: '#f3f3f7', border: '1px solid #e7e8eb', color: '#424752', outline: 'none' }} />
      <button className="w-full mt-2 py-2.5 rounded-xl text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
        Send Report →
      </button>
    </div>
  );
}

function SuccessMock() {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #bbf7d0' }}>
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: '#dcfce7' }}>
          <CheckCircle className="w-6 h-6" style={{ color: '#16a34a' }} />
        </div>
        <p className="font-bold text-sm" style={{ color: '#191c1e' }}>Report Received</p>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold mt-1" style={{ background: '#f3f3f7', color: '#424752' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#16a34a' }} />
          Response time: Within 2 hours
        </div>
      </div>
      <div className="rounded-xl p-3 mb-3" style={{ background: '#f9f9fd' }}>
        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#727783' }}>What happens next</p>
        {[
          'Your care team will review this within 2 hours.',
          'Keep your phone nearby.',
          'If symptoms worsen, report again and choose Urgent.',
        ].map((s, i) => (
          <div key={i} className="flex items-start gap-1.5 mb-1.5">
            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#00478d' }} />
            <p className="text-xs" style={{ color: '#424752' }}>{s}</p>
          </div>
        ))}
      </div>
      <button className="w-full py-2 rounded-xl text-xs font-bold" style={{ background: '#f3f3f7', color: '#191c1e' }}>
        Report Another Issue
      </button>
    </div>
  );
}

function ActionCardMock() {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div className="flex items-center justify-between mb-3">
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}>CRITICAL</span>
        <span className="text-[10px] font-bold flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>
          <Clock className="w-2.5 h-2.5" /> 3h 42m left
        </span>
      </div>
      <div className="flex items-center justify-between mb-0.5">
        <p className="font-bold text-sm" style={{ color: '#191c1e' }}>Alice Johnson</p>
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3" style={{ color: '#dc2626' }} />
          <span className="text-[10px] font-bold" style={{ color: '#dc2626' }}>↑ Worsening</span>
        </div>
      </div>
      <p className="text-[10px] mb-3" style={{ color: '#727783' }}>75 yrs · CHF · 3 days post-discharge · #A12F</p>
      <div className="flex flex-wrap gap-1 mb-3">
        {['CHF','Hypertension','A-Fib'].map(c => <span key={c} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#f1f5f9', color: '#475569' }}>{c}</span>)}
      </div>
      <div className="flex gap-2">
        <button className="flex-1 py-2 rounded-xl text-[10px] font-bold text-white flex items-center justify-center gap-1" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}><Check className="w-2.5 h-2.5" /> Execute</button>
        <button className="px-3 py-2 rounded-xl text-[10px] font-bold border" style={{ background: '#f9f9fd', color: '#424752', borderColor: '#e7e8eb' }}>Snooze</button>
        <button className="px-3 py-2 rounded-xl text-[10px] font-bold border" style={{ background: '#f9f9fd', color: '#424752', borderColor: '#e7e8eb' }}>Delegate</button>
      </div>
    </div>
  );
}

function ContextMock() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div className="px-4 py-3" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#94a3b8' }}>Why now</p>
        <p className="text-xs font-semibold leading-snug" style={{ color: '#1e293b' }}>CHF patient, 3 days post-discharge, reported swelling + dyspnea. Missed Furosemide pickup. Lives alone, no caregiver.</p>
      </div>
      <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#94a3b8' }}>Known conditions</p>
        <div className="flex flex-wrap gap-1">
          {['Congestive Heart Failure','Hypertension','Atrial Fibrillation'].map(c => <span key={c} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#f1f5f9', color: '#475569' }}>{c}</span>)}
        </div>
      </div>
      <div className="px-4 py-2.5 flex flex-wrap gap-x-3 gap-y-1">
        {[['Age','75'],['Meds','4 active'],['Post-discharge','3 days'],['Events (24h)','1'],['Caregiver','None']].map(([k,v]) => (
          <span key={k} className="text-[10px]" style={{ color: '#64748b' }}>
            <span style={{ color: '#94a3b8' }}>{k}: </span>
            <span className="font-bold" style={{ color: k === 'Caregiver' ? '#dc2626' : '#1e293b' }}>{v}</span>
          </span>
        ))}
      </div>
      <div className="px-4 py-3" style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <p className="text-[10px] font-bold" style={{ color: '#94a3b8' }}>Recommended action</p>
        <p className="text-xs font-bold" style={{ color: '#1e293b' }}>Order urgent home visit</p>
      </div>
    </div>
  );
}

function ExecuteMock() {
  const [executed, setExecuted] = useState(false);
  return (
    <div className="rounded-2xl p-5" style={{ background: executed ? '#f0fdf4' : '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: executed ? '1px solid #bbf7d0' : 'none', transition: 'all 0.3s' }}>
      {executed ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>TRACKING</span>
            <button onClick={() => setExecuted(false)} className="text-[10px] font-bold" style={{ color: '#94a3b8' }}>Reset demo</button>
          </div>
          <p className="font-bold text-sm mb-1" style={{ color: '#15803d' }}>Alice Johnson</p>
          <div className="flex items-center gap-1.5 mb-3">
            <CheckCircle className="w-3.5 h-3.5" style={{ color: '#16a34a' }} />
            <p className="text-xs font-semibold" style={{ color: '#166534' }}>Urgent Clinical Review — executed just now</p>
          </div>
          <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(255,255,255,0.6)' }}>
            <p className="text-[10px] font-bold" style={{ color: '#16a34a' }}>Monitoring for stabilization — check back in 2–4 hours</p>
            <div className="w-full rounded-full h-1.5 mt-2" style={{ background: '#bbf7d0' }}>
              <div className="h-1.5 rounded-full w-1/6" style={{ background: '#16a34a' }} />
            </div>
          </div>
          <div className="rounded-xl p-2.5 flex items-center justify-center gap-1.5" style={{ background: '#dcfce7' }}>
            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#16a34a' }} />
            <p className="text-[10px] font-bold" style={{ color: '#166534' }}>Patient notified: "Your care team is on it"</p>
          </div>
        </>
      ) : (
        <>
          <p className="font-bold text-sm mb-1" style={{ color: '#191c1e' }}>Alice Johnson</p>
          <p className="text-xs mb-4" style={{ color: '#727783' }}>Order urgent home visit · CHF post-discharge</p>
          <button
            onClick={() => setExecuted(true)}
            className="w-full py-3 rounded-xl text-xs font-bold text-white transition-all hover:shadow-lg active:scale-95"
            style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}
          >
            <span className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5" /> Execute Action</span>
          </button>
          <p className="text-[10px] text-center mt-2" style={{ color: '#727783' }}>← Click to see the demo</p>
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const HowItWorksPage = () => {
  const [activeSection, setActiveSection] = useState('patient');

  return (
    <div className="min-h-screen font-body" style={{ background: '#f9f9fd', color: '#191c1e' }}>

      {/* ── Sticky nav ── */}
      <nav className="sticky top-0 z-50 bg-[#f9f9fd]/95 backdrop-blur-xl border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-headline font-bold text-sm tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-bold hidden sm:block transition-colors hover:text-[#00478d]" style={{ color: '#424752' }}>← Back to home</Link>
            <Link to="/register" className="text-xs font-bold text-white px-4 py-2 rounded-full" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="py-16 sm:py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%,rgba(0,71,141,0.05),transparent 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" style={{ background: '#d6e3ff', color: '#00478d' }}>
            <Play className="w-3 h-3" /> Full demo walkthrough
          </div>
          <h1 className="font-headline font-extrabold mb-5" style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', letterSpacing: '-0.025em', color: '#191c1e' }}>
            The complete care loop,<br className="hidden sm:block" /> from tap to outcome
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: '#424752' }}>
            SymptoConnect connects patients and clinicians through a continuous intelligence layer. Here's exactly how it works — with interactive demos you can try yourself.
          </p>
        </div>
      </section>

      {/* ── The scenario ── */}
      <section className="py-10 sm:py-14" style={{ background: '#191c1e' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-center mb-8" style={{ color: '#97f2f3' }}>The scenario</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { Icon: User,          iconBg: 'rgba(151,242,243,0.15)', iconColor: '#97f2f3', name: 'Alice Johnson', desc: '75 years old. Congestive heart failure. Discharged 3 days ago. Lives alone. No caregiver.' },
              { Icon: AlertTriangle, iconBg: 'rgba(255,222,168,0.15)', iconColor: '#ffdea8', name: 'The risk', desc: 'Her ankles are swelling. She\'s short of breath. She missed her Furosemide pickup. These are early CHF decompensation signals.' },
              { Icon: Stethoscope,   iconBg: 'rgba(214,227,255,0.15)', iconColor: '#d6e3ff', name: 'Dr. Sarah Chen', desc: 'Alice\'s primary care physician. She has 12 high-risk patients. Without SymptoConnect, she has no way to see Alice\'s signals today.' },
            ].map(({ Icon, iconBg, iconColor, name, desc }) => (
              <div key={name} className="rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
                  <Icon className="w-5 h-5" style={{ color: iconColor }} />
                </div>
                <p className="font-bold text-sm text-white mb-2">{name}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section toggle ── */}
      <div className="sticky top-14 z-40 py-3 border-b" style={{ background: '#f9f9fd/95', backdropFilter: 'blur(12px)', borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center gap-2">
          {[
            { key: 'patient', label: '1  Patient', color: '#97f2f3', text: '#007072' },
            { key: 'engine', label: '2  Intelligence', color: '#d6e3ff', text: '#00478d' },
            { key: 'doctor', label: '3  Clinician', color: '#ffdea8', text: '#5f4300' },
            { key: 'loop', label: '4  Closed loop', color: '#dcfce7', text: '#166534' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className="px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold transition-all"
              style={{
                background: activeSection === s.key ? s.color : 'transparent',
                color: activeSection === s.key ? s.text : '#727783',
                border: `1px solid ${activeSection === s.key ? s.color : '#e7e8eb'}`,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Patient section ── */}
      {activeSection === 'patient' && <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: '#00696b' }}>1</span>
            <div>
              <p className="font-headline font-extrabold text-xl" style={{ color: '#191c1e' }}>Alice reports — in 3 taps</p>
              <p className="text-sm" style={{ color: '#727783' }}>Designed for low-engagement, elderly, and low-literacy patients</p>
            </div>
          </div>

          {/* Timeline pills */}
          <div className="relative mb-4">
            <div
              className="hidden md:block absolute inset-x-0 pointer-events-none"
              style={{ top: '50%', height: '3px', background: '#dde0e5', transform: 'translateY(-50%)', zIndex: 0 }}
            />
            <div className="grid md:grid-cols-3 gap-3" style={{ position: 'relative', zIndex: 1 }}>
              {PATIENT_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-full px-3 py-2.5"
                    style={{ border: `3px solid ${step.whoColor}`, background: '#ffffff', boxShadow: '0 4px 18px rgba(25,28,30,0.08)' }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{ background: '#f9f9fd', boxShadow: '0 2px 10px rgba(25,28,30,0.12)' }}
                    >
                      <Icon className="w-4.5 h-4.5" style={{ color: step.whoText }} />
                    </div>
                    <p className="text-xs font-bold leading-tight" style={{ color: '#191c1e' }}>{step.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots + description + visual */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {PATIENT_STEPS.map((step, i) => (
              <div key={i} className="flex flex-col gap-3">
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: i + 1 }).map((_, j) => (
                    <div key={j} className="w-2 h-2 rounded-full" style={{ background: step.whoText }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{step.description}</p>
                {step.visual}
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-2xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <p className="text-sm font-bold mb-1" style={{ color: '#166534' }}>Design principle</p>
            <p className="text-sm" style={{ color: '#166534' }}>
              Every element is tested against one constraint: would an 83-year-old living alone, unfamiliar with smartphones, be able to complete this after a 15-second explanation? If not, we simplify until the answer is yes.
            </p>
          </div>
        </div>
      </section>}

      {/* ── Intelligence section ── */}
      {activeSection === 'engine' && <section className="py-14 sm:py-20" style={{ background: '#f3f3f7' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: '#00478d' }}>2</span>
            <div>
              <p className="font-headline font-extrabold text-xl" style={{ color: '#191c1e' }}>The risk engine sees the pattern</p>
              <p className="text-sm" style={{ color: '#727783' }}>Five clinical rules, running on every patient signal, every time</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Rule visualization */}
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#727783' }}>Clinical rules engine — Alice's assessment</p>
              {[
                { rule: 'CHF Early Warning', triggered: true, weight: 30, reason: 'Swelling + dyspnea keywords matched within 72h of CHF diagnosis' },
                { rule: 'Recent Discharge Risk', triggered: true, weight: 25, reason: '3 days post-discharge from City General Hospital — highest risk window' },
                { rule: 'Medication Missed', triggered: true, weight: 25, reason: 'Furosemide (diuretic) pickup overdue by 1 day' },
                { rule: 'Social Isolation', triggered: true, weight: 15, reason: 'Lives alone, no designated caregiver' },
                { rule: 'Inactivity Detected', triggered: false, weight: 0, reason: 'No inactivity signal — patient just submitted report' },
              ].map(({ rule, triggered, weight, reason }) => (
                <div key={rule} className="rounded-xl p-3.5" style={{ background: '#ffffff', border: `1px solid ${triggered ? '#bfdbfe' : '#e7e8eb'}` }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: triggered ? '#2563eb' : '#d1d5db' }} />
                      <p className="text-xs font-bold" style={{ color: triggered ? '#1e40af' : '#9ca3af' }}>{rule}</p>
                    </div>
                    {triggered && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#dbeafe', color: '#1d4ed8' }}>+{weight} pts</span>}
                  </div>
                  <p className="text-[10px] ml-4" style={{ color: '#6b7280' }}>{reason}</p>
                </div>
              ))}
            </div>

            {/* Risk score output */}
            <div className="space-y-4">
              <div className="rounded-2xl p-5 sm:p-6" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#727783' }}>Risk assessment output</p>
                <div className="flex items-end gap-3 mb-5">
                  <p className="font-headline font-extrabold" style={{ fontSize: '4rem', letterSpacing: '-0.04em', color: '#dc2626', lineHeight: 1 }}>78</p>
                  <div className="mb-2">
                    <p className="text-xs font-bold" style={{ color: '#dc2626' }}>Overall risk score</p>
                    <p className="text-[10px]" style={{ color: '#9ca3af' }}>Out of 100 · Trend: ↑ worsening</p>
                  </div>
                </div>
                <div className="space-y-2 mb-5">
                  {[
                    ['Readmission risk', 82, '#dc2626'],
                    ['Deterioration risk', 75, '#ea580c'],
                    ['Medication adherence risk', 45, '#d97706'],
                    ['Care breakdown risk', 60, '#ca8a04'],
                  ].map(([label, val, color]) => (
                    <div key={label}>
                      <div className="flex justify-between text-[10px] font-semibold mb-0.5">
                        <span style={{ color: '#6b7280' }}>{label}</span>
                        <span style={{ color }}>{val}%</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full" style={{ background: '#f3f3f7' }}>
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${val}%`, background: color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl p-3" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <p className="text-[10px] font-bold mb-1" style={{ color: '#c2410c' }}>Recommended action</p>
                  <p className="text-xs font-bold" style={{ color: '#9a3412' }}>Urgent Clinical Review — due within 4 hours</p>
                </div>
              </div>

              <div className="rounded-2xl p-4" style={{ background: '#dbeafe', border: '1px solid #bfdbfe' }}>
                <p className="text-xs font-bold mb-1" style={{ color: '#1e40af' }}>No black box</p>
                <p className="text-xs" style={{ color: '#1d4ed8' }}>Every factor is labeled and explainable. A doctor can read the full reasoning behind every risk score — no algorithmic opacity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>}

      {/* ── Doctor section ── */}
      {activeSection === 'doctor' && <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: '#5f4300' }}>3</span>
            <div>
              <p className="font-headline font-extrabold text-xl" style={{ color: '#191c1e' }}>Dr. Chen acts in under 30 seconds</p>
              <p className="text-sm" style={{ color: '#727783' }}>Action cards, not alert lists. Context-first, decision-ready.</p>
            </div>
          </div>

          {/* Timeline pills */}
          <div className="relative mb-4">
            <div
              className="hidden md:block absolute inset-x-0 pointer-events-none"
              style={{ top: '50%', height: '3px', background: '#dde0e5', transform: 'translateY(-50%)', zIndex: 0 }}
            />
            <div className="grid md:grid-cols-3 gap-3" style={{ position: 'relative', zIndex: 1 }}>
              {DOCTOR_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-full px-3 py-2.5"
                    style={{ border: `3px solid ${step.whoColor}`, background: '#ffffff', boxShadow: '0 4px 18px rgba(25,28,30,0.08)' }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{ background: '#f9f9fd', boxShadow: '0 2px 10px rgba(25,28,30,0.12)' }}
                    >
                      <Icon className="w-4.5 h-4.5" style={{ color: step.whoText }} />
                    </div>
                    <p className="text-xs font-bold leading-tight" style={{ color: '#191c1e' }}>{step.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots + description + visual */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {DOCTOR_STEPS.map((step, i) => (
              <div key={i} className="flex flex-col gap-3">
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: i + 1 }).map((_, j) => (
                    <div key={j} className="w-2 h-2 rounded-full" style={{ background: step.whoText }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#424752' }}>{step.description}</p>
                {step.visual}
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-2xl" style={{ background: '#fefce8', border: '1px solid #fde68a' }}>
            <p className="text-sm font-bold mb-1" style={{ color: '#854d0e' }}>Design principle</p>
            <p className="text-sm" style={{ color: '#854d0e' }}>
              Clinicians shouldn't have to read a paragraph to take an action. The card shows the reason in one sentence. The action is one button. The loop closes automatically.
            </p>
          </div>
        </div>
      </section>}

      {/* ── Care breakdown ── */}
      {activeSection === 'loop' && <section className="py-14 sm:py-20" style={{ background: '#f3f3f7' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: '#166534' }}>4</span>
            <div>
              <p className="font-headline font-extrabold text-xl" style={{ color: '#191c1e' }}>The loop closes — and silent patients surface</p>
              <p className="text-sm" style={{ color: '#727783' }}>Real-time notification + proactive care breakdown detection</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Real-time notification */}
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#727783' }}>When Dr. Chen clicks Execute — Alice's phone, instantly</p>
              <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {/* Simulated toast */}
                <div className="rounded-xl p-3 mb-4 flex items-start gap-3" style={{ background: '#191c1e' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(22,163,74,0.2)' }}>
                    <CheckCircle className="w-4 h-4" style={{ color: '#4ade80' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">Your care team is on it</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>A clinician will contact you within 2 hours. Keep your phone nearby.</p>
                  </div>
                </div>
                {/* Notification bell */}
                <div className="rounded-xl p-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#dcfce7', color: '#166534' }}>Care Action</div>
                    <span className="text-[10px]" style={{ color: '#9ca3af' }}>Just now</span>
                  </div>
                  <p className="text-xs font-bold mb-0.5" style={{ color: '#166534' }}>Your care team is on it</p>
                  <p className="text-xs" style={{ color: '#15803d' }}>A clinician will contact you within 2 hours. Keep your phone nearby.</p>
                </div>
              </div>
              <div className="rounded-xl p-4 text-sm" style={{ background: '#dbeafe', border: '1px solid #bfdbfe' }}>
                <p className="font-bold mb-1" style={{ color: '#1e40af' }}>Real-time WebSocket push</p>
                <p style={{ color: '#1d4ed8' }}>Alice's notification fires in under 1 second of Dr. Chen clicking Execute. No polling. No delay. No "check back later."</p>
              </div>
            </div>

            {/* Care breakdown banner */}
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#727783' }}>Meanwhile — Carol was never asked</p>
              <div className="rounded-2xl p-5" style={{ background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {/* Dashboard header mock */}
                <p className="text-xs font-bold mb-3" style={{ color: '#727783' }}>Dr. Chen's dashboard</p>
                {/* Amber banner */}
                <div className="rounded-xl p-4 mb-3" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fef9c3' }}>
                      <AlertTriangle className="w-4 h-4" style={{ color: '#d97706' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold mb-2" style={{ color: '#92400e' }}>1 patient hasn't checked in since discharge</p>
                      <button className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold" style={{ background: '#ffffff', border: '1px solid #fde68a', color: '#92400e' }}>
                        Carol Williams
                        <span style={{ color: '#b45309', opacity: 0.7 }}>· 2d post-discharge</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-3" style={{ background: '#f9f9fd' }}>
                  <p className="text-[10px] font-bold mb-0.5" style={{ color: '#191c1e' }}>Carol Williams, 83</p>
                  <p className="text-[10px]" style={{ color: '#727783' }}>Pneumonia · Discharged 2 days ago · Lives alone · Confused about medications</p>
                </div>
              </div>
              <div className="rounded-xl p-4 text-sm" style={{ background: '#fef9c3', border: '1px solid #fde68a' }}>
                <p className="font-bold mb-1" style={{ color: '#854d0e' }}>Proactive gap detection</p>
                <p style={{ color: '#92400e' }}>Carol didn't report anything — because Carol doesn't know she needs to. SymptoConnect surfaces her automatically when no check-in arrives after discharge.</p>
              </div>
            </div>
          </div>
        </div>
      </section>}

      {/* ── Summary ── */}
      <section className="py-14 sm:py-20" style={{ background: '#191c1e' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-6" style={{ color: '#97f2f3' }}>What you just saw</p>
          <h2 className="font-headline font-extrabold text-white mb-10" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', letterSpacing: '-0.02em' }}>
            The full care loop — closed
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
            {[
              { Icon: MousePointer, iconBg: 'rgba(151,242,243,0.12)', iconColor: '#97f2f3', label: 'Alice tapped',        sub: '3 taps total' },
              { Icon: Brain,        iconBg: 'rgba(214,227,255,0.12)', iconColor: '#d6e3ff', label: 'AI risk detected',    sub: 'Score 78 · CRITICAL' },
              { Icon: Zap,          iconBg: 'rgba(255,222,168,0.12)', iconColor: '#ffdea8', label: 'Doctor acted',        sub: '< 30 seconds' },
              { Icon: CheckCircle,  iconBg: 'rgba(220,252,231,0.12)', iconColor: '#4ade80', label: 'Everyone notified',   sub: 'Patient + family' },
            ].map(({ Icon, iconBg, iconColor, label, sub }) => (
              <div key={label} className="rounded-2xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
                  <Icon className="w-5 h-5" style={{ color: iconColor }} />
                </div>
                <p className="text-xs font-bold text-white mb-0.5">{label}</p>
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{sub}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-sm min-h-[48px] transition-all hover:shadow-xl"
              style={{ background: '#ffffff', color: '#00478d' }}
            >
              <Heart className="w-4 h-4" />
              Try it yourself
            </Link>
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-white text-sm min-h-[48px] transition-all"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
